/* eslint-disable react/no-unused-prop-types */
/**
 * The no-unused-prop-types rule is disabled because ESLint was reporting a false positive for the Props interface, as
 * the `ObserveLimitsCard` is indeed not using some properties from the interface itself. We still need those props
 * though, as they are used in `getWidgetVariant`, `getSiteTrafficContent`, and `getCardContent`.
 */
import type { ComponentProps } from 'react';
import type { TFunction } from 'i18next';

import { Icon, Link } from '@hotjar/hotsauce';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { Trans, useTranslationWithPrefix } from '@hotjar/translations';
import { TrafficCoverageLevel } from '@hotjar/webapp/domains/revenue/sampling/types';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import type { Site } from '@hotjar/site';
import { TrafficGaugeStatus } from '@hotjar/webapp/features/SamplingWidget/SessionTrafficCoverage/TrafficGaugeStatus';

import { DataLimitsCard, WidgetVariant } from '../DataLimitsCard';
import { ObserveLimitsCardStyles as S } from './ObserveLimitsCard.styles';

interface Props {
  site: Site;
  currentTier: FeatureGroup;
  currentPlanAllowance: number;
  dailyTraffic: number;
  coveragePercentage: number;
  daysMeasured: number;
  coverageLevel: TrafficCoverageLevel;
  isMeasuring?: boolean;
  isTrackingIssue?: boolean;
  lastActiveDaysAgo?: number;
  isError?: boolean;
  isLoading?: boolean;
  onVerificationClick: () => unknown;
}

export const getWidgetVariant = ({
  isTrackingIssue,
  isError,
  isLoading,
  coverageLevel,
  daysMeasured,
}: Pick<
  Props,
  'isTrackingIssue' | 'isError' | 'isLoading' | 'coverageLevel' | 'daysMeasured'
>): WidgetVariant => {
  if (isLoading || isError) {
    return WidgetVariant.INFO;
  }

  if (isTrackingIssue) {
    return WidgetVariant.WARNING;
  }

  if (daysMeasured < 7) {
    return WidgetVariant.INFO;
  }

  if (coverageLevel < TrafficCoverageLevel.High) {
    return WidgetVariant.WARNING;
  }

  return WidgetVariant.SUCCESS;
};

const getSiteTrafficContent = (
  t: TFunction,
  { dailyTraffic, isMeasuring, isTrackingIssue, isError, isLoading, daysMeasured }: Props,
): Pick<
  ComponentProps<typeof DataLimitsCard>['infoList'][number],
  'value' | 'valueColor' | 'popover'
> => {
  if (isError) {
    return {
      value: t('site-traffic.values.error'),
      valueColor: 'text_disabled',
    };
  }

  if (isLoading) {
    return {
      value: t('site-traffic.values.loading'),
      valueColor: 'text_disabled',
    };
  }

  if (isTrackingIssue) {
    return {
      value: t('site-traffic.values.tracking-issue'),
      valueColor: 'text_disabled',
    };
  }

  if (isMeasuring && daysMeasured === 0) {
    return {
      value: t('site-traffic.values.measuring'),
      valueColor: 'text_disabled',
    };
  }

  return {
    value: t('site-traffic.values.default', { traffic: abbrNumber(dailyTraffic) }),
    popover: t('site-traffic.tooltip'),
  };
};

const ExternalLink = ({ children, onClick }: { children?: string; onClick?: () => void }) => (
  <Link
    as="button"
    kind="inline"
    onClick={onClick}
    data-testid="data-limits-observe-verify-tracking-code-button"
  >
    {children as string}
  </Link>
);

const getCardContent = (
  t: TFunction,
  {
    isMeasuring,
    isTrackingIssue,
    lastActiveDaysAgo,
    isError,
    daysMeasured,
    coverageLevel,
    coveragePercentage,
    onVerificationClick,
  }: Props,
): JSX.Element | null => {
  if (isError) {
    return (
      <S.CardBody spacing="x1">
        <Icon inline color="text_default" icon="info" size="m" css={{ marginLeft: '0px' }} />
        {t('traffic-coverage.error')}
      </S.CardBody>
    );
  }

  if (isTrackingIssue) {
    return (
      <S.CardBody spacing="x1">
        <Icon inline color="text_default" icon="warning" size="m" css={{ marginLeft: '0px' }} />
        <div>
          <Trans
            i18nKey="billing:product-select-upsell-modal.observe-limits-card.traffic-coverage.tracking-issue-2"
            values={{
              daysWithoutData: lastActiveDaysAgo,
            }}
          >
            <ExternalLink onClick={onVerificationClick} />
          </Trans>
        </div>
      </S.CardBody>
    );
  }

  if (isMeasuring || (daysMeasured > 0 && daysMeasured < 7)) {
    return (
      <S.CardBody spacing="x1">
        <Icon inline color="text_default" icon="info" size="m" css={{ marginLeft: '0px' }} />
        {t('traffic-coverage.measuring')}
      </S.CardBody>
    );
  }

  if (coverageLevel === TrafficCoverageLevel.VeryLow) {
    return (
      <S.CardBody spacing="x1">
        <TrafficGaugeStatus
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete
          isHighlighted={false}
        />
        {t('traffic-coverage.very-low', { coverage: coveragePercentage })}
      </S.CardBody>
    );
  }

  if (coverageLevel === TrafficCoverageLevel.Low) {
    return (
      <S.CardBody spacing="x1">
        <TrafficGaugeStatus
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete
          isHighlighted={false}
        />
        {t('traffic-coverage.low', { coverage: coveragePercentage })}
      </S.CardBody>
    );
  }

  if (coverageLevel === TrafficCoverageLevel.Medium) {
    return (
      <S.CardBody spacing="x1">
        <TrafficGaugeStatus
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete
          isHighlighted={false}
        />
        {t('traffic-coverage.medium', { coverage: coveragePercentage })}
      </S.CardBody>
    );
  }

  if (coverageLevel === TrafficCoverageLevel.High) {
    return (
      <S.CardBody spacing="x1">
        <TrafficGaugeStatus
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete
          isHighlighted={false}
        />
        {t('traffic-coverage.high', { coverage: coveragePercentage })}
      </S.CardBody>
    );
  }

  if (coverageLevel === TrafficCoverageLevel.Max) {
    return (
      <S.CardBody spacing="x1">
        <Icon inline color="text_default" icon="check" size="m" css={{ marginLeft: '5px' }} />
        {t('traffic-coverage.max', { coverage: coveragePercentage })}
      </S.CardBody>
    );
  }

  return null;
};

export const ObserveLimitsCard = (props: Props): JSX.Element => {
  const { currentTier, currentPlanAllowance, isLoading } = props;
  const { t } = useTranslationWithPrefix('billing:product-select-upsell-modal.observe-limits-card');
  const cardContent = getCardContent(t, props);

  return (
    <DataLimitsCard
      infoList={[
        {
          label: t('current-plan.label'),
          value: t('current-plan.value', {
            tier: capitalize(currentTier),
            allowance: abbrNumber(currentPlanAllowance),
          }),
          testIdSpecifier: 'observe-current-plan',
        },
        {
          label: t('site-traffic.label'),
          ...getSiteTrafficContent(t, props),
          testIdSpecifier: 'observe-site-traffic',
        },
      ]}
      widgetVariant={getWidgetVariant(props)}
      isLoading={isLoading}
    >
      <div data-testid="data-limits-observe-card-content">{cardContent}</div>
    </DataLimitsCard>
  );
};
